import { useState } from 'react';
import { useData } from './DataContext.jsx';
import { capitalizeName } from './lib/format.js';
import { Modal, Avatar } from './components.jsx';
import { IconX } from './icons.jsx';

// Admin-side modal: edit a team member's weekly hours and manage their
// recorded absences (sick / vacation). Both are inputs to the weekly
// ranking percentage so absences don't drag down a member's score.
const AVATAR_COLORS = ['#FF6B4A','#10B981','#F59E0B','#6366F1','#EC4899','#0EA5E9','#B45309','#8B5CF6'];

function MemberWorkSettingsModal({ profile, onClose }) {
  const { absences, updateAnyProfile, addAbsence, deleteAbsence } = useData();

  // Stammdaten
  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [initials, setInitials] = useState(profile?.initials || '');
  const [avatarColor, setAvatarColor] = useState(profile?.avatar_color || AVATAR_COLORS[0]);
  const [hourlyRate, setHourlyRate] = useState(String(profile?.hourly_rate ?? 120));
  const [role, setRole] = useState(profile?.role || 'member');
  const [savingMaster, setSavingMaster] = useState(false);
  const [masterStatus, setMasterStatus] = useState(null);
  const [masterErr, setMasterErr] = useState(null);

  const [hours, setHours] = useState(String(profile?.weekly_hours ?? 40));
  const [savingHours, setSavingHours] = useState(false);
  const [hoursStatus, setHoursStatus] = useState(null); // 'saved' | 'error'
  const [hoursErr, setHoursErr] = useState(null);

  const [absKind, setAbsKind] = useState('vacation');
  const [absStart, setAbsStart] = useState('');
  const [absEnd, setAbsEnd] = useState('');
  const [absNote, setAbsNote] = useState('');
  const [addingAbs, setAddingAbs] = useState(false);
  const [absErr, setAbsErr] = useState(null);

  const myAbsences = absences
    .filter(a => a.user_id === profile.id)
    .sort((a, b) => b.start_date.localeCompare(a.start_date));

  async function saveMaster() {
    if (savingMaster) return;
    if (fullName.trim().length < 2) {
      setMasterErr('Anzeigename muss mindestens 2 Zeichen haben.');
      setMasterStatus('error');
      return;
    }
    const rateNum = parseFloat(hourlyRate);
    if (!Number.isFinite(rateNum) || rateNum < 0) {
      setMasterErr('Stundensatz muss eine positive Zahl sein.');
      setMasterStatus('error');
      return;
    }
    setSavingMaster(true); setMasterErr(null); setMasterStatus(null);
    try {
      await updateAnyProfile(profile.id, {
        full_name:    fullName.trim(),
        initials:     initials.trim().toUpperCase() || fullName.trim()[0].toUpperCase(),
        avatar_color: avatarColor,
        hourly_rate:  rateNum,
        role,
      });
      setMasterStatus('saved');
      setTimeout(() => setMasterStatus(null), 2000);
    } catch (e) {
      setMasterErr(e.message || 'Fehler beim Speichern');
      setMasterStatus('error');
    } finally {
      setSavingMaster(false);
    }
  }

  async function saveHours() {
    if (savingHours) return;
    const n = parseFloat(hours);
    if (!Number.isFinite(n) || n < 0 || n > 168) {
      setHoursErr('Bitte einen Wert zwischen 0 und 168 eingeben.');
      setHoursStatus('error');
      return;
    }
    setSavingHours(true); setHoursErr(null); setHoursStatus(null);
    try {
      await updateAnyProfile(profile.id, { weekly_hours: n });
      setHoursStatus('saved');
      setTimeout(() => setHoursStatus(null), 2000);
    } catch (e) {
      setHoursErr(e.message || 'Fehler beim Speichern');
      setHoursStatus('error');
    } finally {
      setSavingHours(false);
    }
  }

  async function addNewAbsence(e) {
    e.preventDefault();
    setAbsErr(null);
    if (!absStart || !absEnd) {
      setAbsErr('Bitte Start- und Enddatum wählen.');
      return;
    }
    if (absEnd < absStart) {
      setAbsErr('Enddatum darf nicht vor dem Startdatum liegen.');
      return;
    }
    setAddingAbs(true);
    try {
      await addAbsence({
        user_id: profile.id,
        kind: absKind,
        start_date: absStart,
        end_date: absEnd,
        note: absNote.trim() || null,
      });
      setAbsStart(''); setAbsEnd(''); setAbsNote('');
    } catch (e) {
      setAbsErr(e.message || 'Fehler beim Anlegen');
    } finally {
      setAddingAbs(false);
    }
  }

  async function removeAbsence(id) {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    try { await deleteAbsence(id); }
    catch (e) { window.alert(e.message || 'Fehler'); }
  }

  return (
    <Modal onClose={onClose}>
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--stroke)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar user={profile} size={36}/>
          <div>
            <div className="eyebrow">Arbeitszeit & Abwesenheiten</div>
            <h2 className="serif" style={{ fontSize: 20, fontWeight: 500, margin: '4px 0 0' }}>
              {capitalizeName(profile.full_name) || profile.email}
            </h2>
          </div>
        </div>
        <button className="icon-btn" onClick={onClose}><IconX size={14}/></button>
      </div>

      <div style={{ padding: 24, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Stammdaten */}
        <div>
          <div className="eyebrow" style={{ marginBottom: 10 }}>Stammdaten</div>
          <div className="grid grid-2" style={{ gap: 12 }}>
            <div className="field">
              <label>Anzeigename *</label>
              <input className="input" value={fullName} onChange={e => setFullName(e.target.value)}/>
            </div>
            <div className="field">
              <label>Initialen <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 500 }}>2 Zeichen</span></label>
              <input className="input" maxLength={3} value={initials}
                     onChange={e => setInitials(e.target.value.toUpperCase())}
                     placeholder="aus Namen abgeleitet"/>
            </div>
            <div className="field">
              <label>Stundensatz <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 500 }}>€/h</span></label>
              <input className="input" type="number" min="0" step="5"
                     value={hourlyRate} onChange={e => setHourlyRate(e.target.value)}/>
            </div>
            <div className="field">
              <label>Rolle</label>
              <div className="seg" style={{ width: '100%' }}>
                <button type="button" className={role === 'member' ? 'on' : ''} onClick={() => setRole('member')} style={{ flex: 1 }}>Member</button>
                <button type="button" className={role === 'admin' ? 'on' : ''} onClick={() => setRole('admin')} style={{ flex: 1 }}>Admin</button>
              </div>
            </div>
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label>Avatar-Farbe</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {AVATAR_COLORS.map(c => (
                  <button key={c} type="button" onClick={() => setAvatarColor(c)} style={{
                    width: 28, height: 28, borderRadius: 8,
                    border: avatarColor === c ? '2px solid var(--ink)' : '2px solid transparent',
                    background: c, cursor: 'pointer', padding: 0, outline: 0,
                  }}/>
                ))}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
            <div className="tiny" style={{
              flex: 1,
              color: masterStatus === 'saved' ? 'var(--ok)' : masterStatus === 'error' ? 'var(--crit)' : 'var(--ink-3)',
            }}>
              {masterStatus === 'saved' && '✓ Gespeichert'}
              {masterStatus === 'error' && masterErr}
              {!masterStatus && 'E-Mail wird zentral via Supabase verwaltet (Auth → Users).'}
            </div>
            <button className="btn btn-primary" onClick={saveMaster} disabled={savingMaster}>
              {savingMaster ? 'Speichere…' : 'Stammdaten speichern'}
            </button>
          </div>
        </div>

        <hr className="div" style={{ margin: 0 }}/>

        {/* Weekly hours */}
        <div>
          <div className="eyebrow" style={{ marginBottom: 10 }}>Wochenarbeitszeit</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              className="input" type="number" min="0" max="168" step="0.5"
              value={hours} onChange={e => setHours(e.target.value)}
              style={{ width: 120 }}
            />
            <span className="muted" style={{ fontSize: 13 }}>Stunden / Woche</span>
            <div style={{ flex: 1 }}/>
            <button className="btn btn-primary" onClick={saveHours} disabled={savingHours}>
              {savingHours ? 'Speichere…' : 'Speichern'}
            </button>
          </div>
          <div className="tiny muted" style={{ marginTop: 8, lineHeight: 1.5 }}>
            Grundlage für den Wochen-Wettbewerb. Bei Krankheit oder Urlaub wird das Soll für die betreffenden Werktage automatisch reduziert.
          </div>
          {hoursStatus === 'saved' && <div className="tiny" style={{ color: 'var(--ok)', marginTop: 6 }}>✓ Gespeichert</div>}
          {hoursStatus === 'error' && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 6 }}>{hoursErr}</div>}
        </div>

        {/* Add absence */}
        <div>
          <div className="eyebrow" style={{ marginBottom: 10 }}>Abwesenheit eintragen</div>
          <form onSubmit={addNewAbsence} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div className="seg">
                <button type="button" className={absKind === 'vacation' ? 'on' : ''} onClick={() => setAbsKind('vacation')}>🏖 Urlaub</button>
                <button type="button" className={absKind === 'sick' ? 'on' : ''} onClick={() => setAbsKind('sick')}>🤒 Krank</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label>Von</label>
                <input className="input" type="date" value={absStart} onChange={e => setAbsStart(e.target.value)} required/>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label>Bis</label>
                <input className="input" type="date" value={absEnd} onChange={e => setAbsEnd(e.target.value)} required/>
              </div>
            </div>
            <input className="input" placeholder="Notiz (optional)" value={absNote} onChange={e => setAbsNote(e.target.value)}/>
            {absErr && <div className="tiny" style={{ color: 'var(--crit)' }}>{absErr}</div>}
            <button type="submit" className="btn btn-primary" disabled={addingAbs}
                    style={{ alignSelf: 'flex-start' }}>
              {addingAbs ? 'Lege an…' : 'Eintragen'}
            </button>
          </form>
        </div>

        {/* Existing absences */}
        <div>
          <div className="eyebrow" style={{ marginBottom: 10 }}>
            Erfasste Abwesenheiten {myAbsences.length > 0 && `(${myAbsences.length})`}
          </div>
          {myAbsences.length === 0 ? (
            <div className="tiny muted">Keine Einträge.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {myAbsences.map(a => (
                <div key={a.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 12px', borderRadius: 10,
                  background: 'var(--bg-sunken)', border: '1px solid var(--stroke)',
                }}>
                  <div style={{ fontSize: 16 }}>{a.kind === 'sick' ? '🤒' : '🏖'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {a.kind === 'sick' ? 'Krank' : 'Urlaub'} · {new Date(a.start_date).toLocaleDateString('de-DE')} – {new Date(a.end_date).toLocaleDateString('de-DE')}
                    </div>
                    {a.note && <div className="tiny muted" style={{ marginTop: 2 }}>{a.note}</div>}
                  </div>
                  <button className="btn btn-ghost" style={{ padding: '4px 10px', color: 'var(--crit)' }} onClick={() => removeAbsence(a.id)}>
                    Löschen
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '14px 24px', borderTop: '1px solid var(--stroke)', display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost" onClick={onClose}>Schließen</button>
      </div>
    </Modal>
  );
}

export { MemberWorkSettingsModal };
